import {
  CognitoUserSession,
  CognitoIdToken,
  CognitoRefreshToken,
  CognitoAccessToken,
} from 'amazon-cognito-identity-js';
import { service } from '@ember/service';
import RSVP from 'rsvp';
import ENV from 'scaleflow/config/environment';
import { DateTime } from 'scaleflow/utils/date-time';
import BaseAuthenticator from 'scaleflow/authenticators/BaseAuthenticator';
import RefreshError, {
  terminalRefreshError,
  transientRefreshError,
} from 'scaleflow/authenticators/refresh-errors';

const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_BACKOFFS_MS = [300, 1200]; // delays before attempts 2 and 3
const REFRESH_TIMEOUT_MS = 8000;

// Cognito's token endpoint returns `invalid_grant` when the refresh token is
// expired/revoked and `invalid_client` for client misconfig — both fatal. A
// 401 is likewise fatal. Everything else (429, 5xx, network, timeout) is
// transient and worth retrying.
function isTerminalRefreshStatus(status, errorText = '') {
  if (status === 401) return true;
  if (status === 400) return /invalid_grant|invalid_client/.test(errorText);
  return false;
}

class OAuth2Atuhenticator extends BaseAuthenticator {
  @service session;

  endpoint = ENV.cognito.domain;

  authenticate({ code, clientId, refresh = false }) {
    this.clientId = clientId ?? ENV.cognito.clientId;
    if (refresh)
      return this.refreshSession(this.session.data.authenticated, true);
    return this.getTokensFromCode(code);
  }

  async getTokensFromCode(code) {
    try {
      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };

      const body = new URLSearchParams({
        grant_type: 'authorization_code',
        code: code,
        redirect_uri: window.location.origin + '/authenticate',
        client_id: this.clientId,
      });

      const response = await fetch(`${this.endpoint}/oauth2/token`, {
        method: 'POST',
        headers,
        body,
      });

      if (response.status !== 200) throw new Error('Failed to authenticate');

      const data = await response.json();
      const { access_token, refresh_token, id_token } = data;
      const session = this.createCognitoUserSession(
        access_token,
        refresh_token,
        id_token,
      );
      const session_object = this.extractUserSessionDetails(session);
      if (session_object.error) return RSVP.reject();
      return { details: session_object };
    } catch (err) {
      console.debug('Oauth2 Authenticate error:', err);
      return RSVP.reject();
    }
  }

  extractUserSessionDetails(session) {
    const tenants = this.tenants(
      session.accessToken?.payload?.['cognito:groups'],
    );

    if (tenants.length === 0)
      return {
        error: true,
        message:
          'User does not belong to any tenant. Please contact your administrator.',
      };
    const accessToken = session.accessToken.jwtToken;
    const refreshToken = session.refreshToken.token;
    const idToken = session.idToken.jwtToken;
    const email = session.idToken.payload.email;
    const sub = session.idToken.payload.sub;
    const expiry = DateTime.fromUnixSeconds(
      session.idToken.payload.exp,
    ).toDate();
    const all_tenants = session.accessToken?.payload?.['cognito:groups'];
    const session_object = {
      tenants,
      accessToken,
      refreshToken,
      idToken,
      email,
      expiry,
      all_tenants,
      sub,
      clientId: this.clientId,
    };
    return session_object;
  }

  createCognitoUserSession(access_token, refresh_token, id_token) {
    const cognito_user_session = new CognitoUserSession({
      IdToken: new CognitoIdToken({
        IdToken: id_token,
      }),
      RefreshToken: new CognitoRefreshToken({
        RefreshToken: refresh_token,
      }),
      AccessToken: new CognitoAccessToken({
        AccessToken: access_token,
      }),
    });

    return cognito_user_session;
  }

  async fetchRefreshedTokens(refreshToken) {
    const headers = {
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.clientId,
      refresh_token: refreshToken,
    });

    let lastError;
    for (let attempt = 0; attempt < REFRESH_MAX_ATTEMPTS; attempt++) {
      if (attempt > 0) {
        const delay = REFRESH_BACKOFFS_MS[attempt - 1] ?? 0;
        await new Promise((resolve) => setTimeout(resolve, delay));
      }

      const controller = new AbortController();
      const timeoutId = setTimeout(
        () => controller.abort(),
        REFRESH_TIMEOUT_MS,
      );

      let response;
      try {
        response = await fetch(`${this.endpoint}/oauth2/token`, {
          method: 'POST',
          headers,
          body,
          signal: controller.signal,
        });
      } catch (err) {
        // Network failure or timeout (AbortError) — transient, retry.
        lastError = transientRefreshError(
          `Token refresh network error: ${err?.name ?? 'unknown'}`,
        );
        continue;
      } finally {
        clearTimeout(timeoutId);
      }

      if (response.ok) {
        const json = await response.json();
        const { access_token, refresh_token, id_token } = json;
        return { access_token, refresh_token, id_token };
      }

      const errorText = await response.text().catch(() => '');
      console.error('Token refresh failed:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        attempt: attempt + 1,
      });

      // A dead grant will never recover — fail immediately so the caller can
      // log the user out. Throttling / 5xx are worth another attempt.
      if (isTerminalRefreshStatus(response.status, errorText)) {
        throw terminalRefreshError(
          `Token refresh rejected (status ${response.status}): ${errorText}`,
        );
      }

      lastError = transientRefreshError(
        `Token refresh failed transiently (status ${response.status})`,
      );
    }

    // Exhausted retries on transient failures — keep the session, let the
    // request fail. Default unknown to transient (fail-safe = stay logged in).
    throw lastError ?? transientRefreshError('Token refresh failed');
  }

  async getSessionObjectFromRefreshToken(refreshToken) {
    if (!refreshToken) throw terminalRefreshError('No refresh token provided');
    const { access_token, id_token } =
      await this.fetchRefreshedTokens(refreshToken);
    const congitoUserSession = this.createCognitoUserSession(
      access_token,
      refreshToken,
      id_token,
    );
    const session_object = this.extractUserSessionDetails(congitoUserSession);
    return { details: session_object.error ? undefined : session_object };
  }

  async refreshSession(data, forceRefresh = false) {
    this.clientId = data?.details?.clientId ?? ENV.cognito.clientId;
    try {
      if (!data?.details) {
        console.error('Invalid session data provided for refresh');
        return RSVP.reject(terminalRefreshError('Invalid session data'));
      }
      let shouldRefresh = true;
      const { accessToken, refreshToken, idToken } = data.details;

      if (forceRefresh)
        return await this.getSessionObjectFromRefreshToken(refreshToken);

      if (!accessToken && !idToken && refreshToken) shouldRefresh = true;
      else if (accessToken && idToken && refreshToken) {
        const session = this.createCognitoUserSession(
          accessToken,
          refreshToken,
          idToken,
        );
        const now = DateTime.now();
        const tokenExp = DateTime.fromUnixSeconds(
          session?.accessToken?.payload?.exp,
        );
        const isExpired = tokenExp.isSameOrBefore(now);
        if (isExpired) shouldRefresh = true;
        else {
          const session_object = this.extractUserSessionDetails(session);
          return { details: session_object.error ? undefined : session_object };
        }
      }
      if (shouldRefresh) {
        return await this.getSessionObjectFromRefreshToken(refreshToken);
      }
    } catch (err) {
      console.debug('Oauth2 restore err:', err);
      // Preserve the terminal/transient classification so callers can decide
      // whether to log out. Anything unclassified defaults to transient.
      return RSVP.reject(
        err instanceof RefreshError
          ? err
          : transientRefreshError(err?.message ?? 'Token refresh failed'),
      );
    }
  }

  async restore(data) {
    try {
      const session = await this.refreshSession(data);
      if (session === undefined) return RSVP.reject();
      return session;
    } catch (err) {
      return RSVP.reject(err);
    }
  }
}

export default OAuth2Atuhenticator;
